/**
 * Utility functions for transforming Saleor variant data.
 *
 * These pure functions handle data transformation separately from presentation,
 * making it easy to customize how Saleor data is interpreted.
 */

import type { VariantOption, AttributeGroup } from "./types";
import { getColorHex, isColorAttribute, isSizeAttribute, COLOR_NAME_TO_HEX } from "@/lib/colors";
import { getMaxDiscountInfo as getMaxDiscountInfoBase } from "@/lib/pricing";
import {
	compareStrengthNames,
	comparePackageNames,
	isPurchasableVariant,
} from "@/lib/packaging/variant-order";
import { sortBySizeProperty } from "@/lib/sizes";

// Re-export for backwards compatibility
export { COLOR_NAME_TO_HEX };

const DOSAGE_SLUGS = new Set([
	// en        de            es       fr       it          nl         pt         pl
	"dosage",
	"dosierung",
	"dosis",
	"dosage",
	"dosaggio",
	"dosering",
	"dosagem",
	"dawka",
	"strength",
]);
const PACKAGE_SLUGS = new Set([
	// en
	"package",
	"packaging",
	"pack",
	"bundle",
	"quantity",
	"amount",
	// de
	"gebinde",
	"menge",
	"anzahl",
	"packung",
	"verpackung",
	// es
	"cantidad",
	// fr
	"quantite",
	"quantité",
	// it
	"quantita",
	"quantità",
	// nl
	"hoeveelheid",
	"aantal",
	// pt
	"quantidade",
	// pl
	"ilosc",
	"ilość",
]);

function normalizeAttributeSlug(slug: string): string {
	const normalized = slug.toLowerCase().trim();
	if (DOSAGE_SLUGS.has(normalized)) return "dosage";
	if (PACKAGE_SLUGS.has(normalized)) return "package";
	return normalized;
}

function isDosageAttribute(slug: string): boolean {
	return normalizeAttributeSlug(slug) === "dosage";
}

function isPackageAttribute(slug: string): boolean {
	return normalizeAttributeSlug(slug) === "package";
}

function extractPackageInfo(optionName: string): { unitCount: number; unitLabel?: string } | null {
	// Match "1 Pack", "2 Pack", "3 Pack", "10 Pack" (+ de: Packung)
	const packMatch = optionName.match(/(\d+)\s*(?:Pack(?:ung)?)/i);
	if (packMatch) {
		return { unitCount: parseInt(packMatch[1]!, 10) };
	}
	// Match unit-based names across all 8 locales:
	// en: Vial(s), Piece(s)  |  de: Stück, Fläschchen  |  es: Unidad(es), Vial(es)
	// fr: Pièce(s), Flacon(s)  |  it: Pezzo/Pezzi, Fiala/Fiale
	// nl: Stuk(s), Flesje(s)  |  pt: Unidade(s), Frasco(s)  |  pl: Sztuk(a/i), Szt.
	const unitMatch = optionName.match(
		/(\d+)\s*(?:vials?|stück|fläschchen|pieces?|unidad(?:es)?|pi[eè]ces?|flacons?|pezz[oi]|fial[ae]|stuks?|flesjes?|unidades?|frascos?|sztuk[ai]?|szt\.?)/i,
	);
	if (unitMatch) {
		return { unitCount: parseInt(unitMatch[1]!, 10), unitLabel: "Vial" };
	}
	return null;
}

function getUnitPrice(
	variants: SaleorVariant[],
	unitCount?: number,
): { amount: number; currency: string } | undefined {
	if (!unitCount || unitCount <= 0) return undefined;

	const prices = variants
		.map((variant) => variant.pricing?.price?.gross)
		.filter((price): price is { amount: number; currency: string } => Boolean(price));

	if (prices.length === 0) return undefined;

	const currencies = new Set(prices.map((price) => price.currency));
	if (currencies.size !== 1) return undefined;

	const amounts = [...new Set(prices.map((price) => price.amount))];
	if (amounts.length !== 1) return undefined;

	return {
		amount: amounts[0] / unitCount,
		currency: prices[0].currency,
	};
}

/**
 * Raw variant type from Saleor GraphQL
 */
export type SaleorVariant = {
	id: string;
	name: string;
	quantityAvailable?: number | null;
	/** Saleor usually returns []; null/undefined is tolerated at runtime. */
	selectionAttributes?: Array<{
		attribute: { slug?: string | null; name?: string | null };
		values: Array<{ name?: string | null; value?: string | null }> | null;
	}> | null;
	nonSelectionAttributes?: Array<{
		attribute: { slug?: string | null; name?: string | null };
		values: Array<{ name?: string | null; value?: string | null }>;
	}>;
	pricing?: {
		price?: { gross: { amount: number; currency: string } } | null;
		priceUndiscounted?: { gross: { amount: number; currency: string } } | null;
	} | null;
};

// ============================================================================
// Discount Helpers (using shared pricing utilities)
// ============================================================================

/**
 * Get max discount info across a list of variants.
 */
function getMaxDiscountInfo(variants: SaleorVariant[]): { hasDiscount: boolean; maxPercent: number } {
	const result = getMaxDiscountInfoBase(variants, (v) => ({
		current: v.pricing?.price?.gross?.amount,
		undiscounted: v.pricing?.priceUndiscounted?.gross?.amount,
	}));
	return {
		hasDiscount: result.isOnSale,
		maxPercent: result.discountPercent ?? 0,
	};
}

// ============================================================================
// Main Functions
// ============================================================================

/**
 * Group variants by their attributes.
 *
 * For a product with Color (Black, White) and Size (S, M, L), this returns:
 * [
 *   { slug: "color", name: "Color", options: [{ id: "black", name: "Black", colorHex: "#1a1a1a", ... }, ...] },
 *   { slug: "size", name: "Size", options: [{ id: "s", name: "S", ... }, ...] }
 * ]
 *
 * Each option tracks which variants it appears in, allowing us to:
 * - Show availability based on other selections
 * - Find the matching variant when all attributes are selected
 *
 * NOTE: Attributes with only one unique value are filtered out, as they don't
 * differentiate variants (likely product-level attributes incorrectly assigned to variants).
 */
export function groupVariantsByAttributes(variants: SaleorVariant[]): AttributeGroup[] {
	// Map: attributeSlug -> { name, values: Map<valueName, { variantIds, colorHex }> }
	const attributeMap = new Map<
		string,
		{
			name: string;
			values: Map<
				string,
				{ variantIds: Set<string>; colorHex?: string; unitCount?: number; unitLabel?: string }
			>;
		}
	>();

	// Process each variant
	for (const variant of variants) {
		for (const attr of variant.selectionAttributes ?? []) {
			const slug = normalizeAttributeSlug(attr.attribute.slug ?? "");
			const name = attr.attribute.name ?? slug;

			if (!attributeMap.has(slug)) {
				attributeMap.set(slug, { name, values: new Map() });
			}

			const attrData = attributeMap.get(slug)!;

			for (const val of attr.values ?? []) {
				const valueName = val.name ?? "";
				if (!valueName) continue;

				if (!attrData.values.has(valueName)) {
					const packageInfo = isPackageAttribute(slug) ? extractPackageInfo(valueName) : undefined;
					attrData.values.set(valueName, {
						variantIds: new Set(),
						colorHex: isColorAttribute(slug) ? getColorHex(val) : undefined,
						unitCount: packageInfo?.unitCount,
						unitLabel: packageInfo?.unitLabel,
					});
				}

				attrData.values.get(valueName)!.variantIds.add(variant.id);
			}
		}
	}

	// Convert to AttributeGroup array
	const groups: AttributeGroup[] = [];

	for (const [slug, data] of attributeMap) {
		const options: VariantOption[] = [];

		for (const [valueName, valueData] of data.values) {
			// Get all variants with this value
			const variantsWithValue = [...valueData.variantIds]
				.map((id) => variants.find((v) => v.id === id)!)
				.filter(Boolean);

			// Check availability (null = unlimited stock / tracking disabled → available)
			const available = variantsWithValue.some(
				(v) => v.quantityAvailable === null || v.quantityAvailable === undefined || v.quantityAvailable > 0,
			);

			// Get discount info
			const { hasDiscount, maxPercent } = getMaxDiscountInfo(variantsWithValue);

			options.push({
				id: valueName.toLowerCase().replace(/\s+/g, "-"),
				name: valueName,
				available,
				hasDiscount,
				discountPercent: maxPercent > 0 ? maxPercent : undefined,
				colorHex: valueData.colorHex,
				variantIds: [...valueData.variantIds],
				unitCount: valueData.unitCount,
				unitLabel: valueData.unitLabel,
			});
		}

		// Sort size options in logical order (S, M, L, XL, etc.)
		const sortedOptions = isDosageAttribute(slug)
			? [...options].sort((a, b) => compareStrengthNames(a.name, b.name))
			: isPackageAttribute(slug)
				? [...options].sort((a, b) => comparePackageNames(a.name, b.name))
				: isSizeAttribute(slug)
					? sortBySizeProperty(options)
					: options;

		groups.push({ slug, name: data.name, options: sortedOptions });
	}

	// Sort: color attributes first, then size, then others
	groups.sort((a, b) => {
		const aIsDosage = isDosageAttribute(a.slug);
		const bIsDosage = isDosageAttribute(b.slug);
		const aIsPackage = isPackageAttribute(a.slug);
		const bIsPackage = isPackageAttribute(b.slug);
		const aIsColor = isColorAttribute(a.slug);
		const bIsColor = isColorAttribute(b.slug);
		const aIsSize = isSizeAttribute(a.slug);
		const bIsSize = isSizeAttribute(b.slug);

		if (aIsDosage && !bIsDosage) return -1;
		if (!aIsDosage && bIsDosage) return 1;
		if (aIsPackage && !bIsPackage) return -1;
		if (!aIsPackage && bIsPackage) return 1;
		if (aIsColor && !bIsColor) return -1;
		if (!aIsColor && bIsColor) return 1;
		if (aIsSize && !bIsSize) return -1;
		if (!aIsSize && bIsSize) return 1;
		return 0;
	});

	return groups;
}

/**
 * Find the variant that matches all selected attribute values.
 *
 * IMPORTANT: Only returns a match if ALL attribute groups have a selection.
 * Partial selections (e.g., only Color selected, Size not selected) return undefined.
 */
export function findMatchingVariant(
	variants: SaleorVariant[],
	selections: Record<string, string>,
	precomputedGroups?: AttributeGroup[],
): string | undefined {
	const selectionEntries = Object.entries(selections).filter(([, value]) => value);
	if (selectionEntries.length === 0) return undefined;

	// Get all attribute groups to verify all are selected
	const attributeGroups = precomputedGroups ?? groupVariantsByAttributes(variants);
	const allAttributesSelected = attributeGroups.every(
		(group) => selections[group.slug] !== undefined && selections[group.slug] !== "",
	);

	if (!allAttributesSelected) return undefined;

	for (const variant of variants) {
		let allMatch = true;

		for (const [attrSlug, selectedValue] of selectionEntries) {
			const attr = (variant.selectionAttributes ?? []).find(
				(a) => normalizeAttributeSlug(a.attribute.slug ?? "") === normalizeAttributeSlug(attrSlug),
			);

			if (!attr) {
				allMatch = false;
				break;
			}

			const hasMatchingValue = (attr.values ?? []).some(
				(v) => (v.name ?? "").toLowerCase().replace(/\s+/g, "-") === selectedValue.toLowerCase(),
			);

			if (!hasMatchingValue) {
				allMatch = false;
				break;
			}
		}

		if (allMatch) return variant.id;
	}

	return undefined;
}

/**
 * Get current selections from a variant ID.
 * Reverse lookup: given a variant ID, extract its attribute values.
 */
export function getSelectionsFromVariant(
	variants: SaleorVariant[],
	variantId: string,
): Record<string, string> {
	const variant = variants.find((v) => v.id === variantId);
	if (!variant) return {};

	const selections: Record<string, string> = {};
	for (const attr of variant.selectionAttributes ?? []) {
		const slug = normalizeAttributeSlug(attr.attribute.slug ?? "");
		const value = (attr.values ?? [])[0]?.name ?? "";
		if (slug && value) {
			selections[slug] = value.toLowerCase().replace(/\s+/g, "-");
		}
	}

	return selections;
}

/** Select one real, purchasable combination, with strength before package size. */
export function getDefaultSelections(
	attributeGroups: AttributeGroup[],
	variants?: SaleorVariant[],
): Record<string, string> {
	let candidates = variants?.filter(isPurchasableVariant);
	const selections: Record<string, string> = {};
	for (const group of attributeGroups) {
		const options = isDosageAttribute(group.slug)
			? [...group.options].sort((a, b) => compareStrengthNames(a.name, b.name))
			: isPackageAttribute(group.slug)
				? [...group.options].sort((a, b) => comparePackageNames(a.name, b.name))
				: group.options;
		const option = options.find((option) =>
			candidates ? candidates.some((variant) => option.variantIds?.includes(variant.id)) : option.available,
		);
		if (!option) return {};
		selections[group.slug] = option.id;
		if (candidates) candidates = candidates.filter((variant) => option.variantIds?.includes(variant.id));
	}
	return selections;
}

/**
 * Get options for an attribute with availability and compatibility info.
 *
 * Options are marked as:
 * - `available: true` - In stock
 * - `available: false` - Out of stock globally
 * - `existsWithCurrentSelection: true` - Variant exists with this + current selections
 * - `existsWithCurrentSelection: false` - No variant exists (will clear other selections)
 */
export function getOptionsForAttribute(
	variants: SaleorVariant[],
	attributeGroups: AttributeGroup[],
	currentSelections: Record<string, string>,
	targetAttributeSlug: string,
): VariantOption[] {
	const targetGroup = attributeGroups.find((g) => g.slug === targetAttributeSlug);
	if (!targetGroup) return [];

	const otherSelections = Object.entries(currentSelections).filter(([slug]) => slug !== targetAttributeSlug);
	const shouldShowUnitPrice = isPackageAttribute(targetAttributeSlug);

	// Pre-compute: find the single-pack option to calculate savings for multi-pack options
	const singlePackOption = shouldShowUnitPrice
		? targetGroup.options.find((o) => (o.unitCount ?? 0) === 1)
		: undefined;
	const singlePackVariants = singlePackOption
		? variants.filter((variant) => {
				const attr = (variant.selectionAttributes ?? []).find(
					(a) =>
						normalizeAttributeSlug(a.attribute.slug ?? "") === normalizeAttributeSlug(targetAttributeSlug),
				);
				return (attr?.values ?? []).some(
					(v) => (v.name ?? "").toLowerCase().replace(/\s+/g, "-") === singlePackOption.id,
				);
			})
		: [];
	const singlePackPrice =
		singlePackVariants.length > 0
			? singlePackVariants
					.map((v) => v.pricing?.price?.gross?.amount)
					.find((amount): amount is number => typeof amount === "number")
			: undefined;

	return targetGroup.options.map((option) => {
		// Find ALL variants that have this option value
		const variantsWithOption = variants.filter((variant) => {
			const attr = (variant.selectionAttributes ?? []).find(
				(a) => normalizeAttributeSlug(a.attribute.slug ?? "") === normalizeAttributeSlug(targetAttributeSlug),
			);
			return (attr?.values ?? []).some(
				(v) => (v.name ?? "").toLowerCase().replace(/\s+/g, "-") === option.id,
			);
		});

		// Check availability (null = unlimited stock / tracking disabled → available)
		const available = variantsWithOption.some(
			(v) => v.quantityAvailable === null || v.quantityAvailable === undefined || v.quantityAvailable > 0,
		);
		const { hasDiscount, maxPercent } = getMaxDiscountInfo(variantsWithOption);

		// Check if a variant exists with this option AND all other current selections
		let existsWithCurrentSelection = true;
		let compatibleVariants = variantsWithOption;
		if (otherSelections.length > 0) {
			compatibleVariants = variantsWithOption.filter((variant) => {
				for (const [attrSlug, selectedValue] of otherSelections) {
					const attr = (variant.selectionAttributes ?? []).find(
						(a) => normalizeAttributeSlug(a.attribute.slug ?? "") === normalizeAttributeSlug(attrSlug),
					);
					if (!attr) return false;

					const hasValue = (attr.values ?? []).some(
						(v) => (v.name ?? "").toLowerCase().replace(/\s+/g, "-") === selectedValue,
					);
					if (!hasValue) return false;
				}
				return true;
			});
			existsWithCurrentSelection = compatibleVariants.length > 0;
		}

		const unitPriceBase = compatibleVariants.length > 0 ? compatibleVariants : variantsWithOption;
		const unitPrice = shouldShowUnitPrice ? getUnitPrice(unitPriceBase, option.unitCount) : undefined;

		// Compute savings: (singlePrice × packCount) - packPrice, only for multi-pack options
		let savingsAmount: number | undefined;
		if (shouldShowUnitPrice && singlePackPrice != null && (option.unitCount ?? 0) > 1) {
			const packPrice = unitPriceBase
				.map((v) => v.pricing?.price?.gross?.amount)
				.find((amount): amount is number => typeof amount === "number");
			if (packPrice != null) {
				const rawSavings = singlePackPrice * (option.unitCount ?? 0) - packPrice;
				if (rawSavings > 0) savingsAmount = Math.round(rawSavings * 100) / 100;
			}
		}

		return {
			...option,
			available,
			hasDiscount,
			discountPercent: maxPercent > 0 ? maxPercent : undefined,
			existsWithCurrentSelection,
			unitPrice,
			savingsAmount,
		};
	});
}

/**
 * Smart selection handler that adjusts other attributes when needed.
 * When user selects an option that doesn't exist with current selections,
 * we clear conflicting selections rather than blocking the user.
 */
export function getAdjustedSelections(
	variants: SaleorVariant[],
	currentSelections: Record<string, string>,
	attributeSlug: string,
	newValue: string,
	precomputedGroups?: AttributeGroup[],
): Record<string, string> {
	const newSelections = { ...currentSelections, [attributeSlug]: newValue };
	const matchingVariant = findMatchingVariant(variants, newSelections, precomputedGroups);

	if (matchingVariant) return newSelections;

	// No exact match - keep only the newly selected attribute
	return { [attributeSlug]: newValue };
}

// Backwards compatibility alias
export const getAvailableOptionsForAttribute = getOptionsForAttribute;

/**
 * Check if any attribute group has no available options given current selections.
 * Returns info about "dead end" situations.
 */
export function getUnavailableAttributeInfo(
	variants: SaleorVariant[],
	attributeGroups: AttributeGroup[],
	currentSelections: Record<string, string>,
): { slug: string; name: string; blockedBy: string } | null {
	const selectionEntries = Object.entries(currentSelections).filter(([, value]) => value);
	if (selectionEntries.length === 0) return null;

	for (const group of attributeGroups) {
		if (currentSelections[group.slug]) continue;

		const options = getOptionsForAttribute(variants, attributeGroups, currentSelections, group.slug);
		const hasAnyAvailable = options.some((opt) => opt.available && opt.existsWithCurrentSelection !== false);

		if (!hasAnyAvailable) {
			const blockingSelection = selectionEntries[selectionEntries.length - 1];
			const blockingGroup = attributeGroups.find((g) => g.slug === blockingSelection[0]);
			const blockingOption = blockingGroup?.options.find((o) => o.id === blockingSelection[1]);

			return {
				slug: group.slug,
				name: group.name,
				blockedBy: blockingOption?.name || blockingSelection[1],
			};
		}
	}

	return null;
}
